/**
 * Un bloque del cuestionario dentro del formulario: su intención arriba y sus
 * tres preguntas como campos.
 *
 * Es un `fieldset` con `legend` de verdad y no un div con un título: el bloque
 * agrupa tres campos y esa agrupación tiene que existir para quien navega con
 * teclado o lector de pantalla, no solo para quien la ve.
 *
 * Una respuesta vacía se rotula **"Pendiente"**, no "Sin dato": la pregunta
 * existe y espera respuesta, y el contrato es explícito en eso.
 */
import { PREGUNTAS_POR_BLOQUE, type InterviewBlockDef } from '@ops/contracts'

import { Icon, Stack } from '../../ui'
import { claveDe } from './borrador'

export interface BloqueDePreguntasProps {
  def: InterviewBlockDef
  /** Texto actual de cada pregunta, por clave `bloque-nro`. */
  borrador: Readonly<Record<string, string>>
  onCambio: (clave: string, valor: string) => void
  /** Deshabilita los campos mientras se guarda. */
  deshabilitado?: boolean
}

export function BloqueDePreguntas({
  def,
  borrador,
  onCambio,
  deshabilitado,
}: BloqueDePreguntasProps) {
  const respondidas = def.preguntas.filter((_, i) => {
    const valor = borrador[claveDe(def.bloque, i + 1)] ?? ''
    return valor.trim() !== ''
  }).length

  return (
    <fieldset
      data-testid={`bloque-${def.bloque}`}
      className="min-w-0 border-0 p-0"
    >
      <legend className="mb-1 flex w-full flex-wrap items-baseline justify-between gap-x-3">
        <span className="text-titulo font-semibold text-text">
          {def.bloque} · {def.titulo}
        </span>
        <span className="tabular text-micro text-muted-strong">
          {respondidas} de {PREGUNTAS_POR_BLOQUE} respondidas
        </span>
      </legend>
      <p className="mb-3 text-meta text-muted-strong">{def.intencion}</p>

      <Stack gap="md">
        {def.preguntas.map((pregunta, i) => {
          const nro = i + 1
          const clave = claveDe(def.bloque, nro)
          const valor = borrador[clave] ?? ''
          const vacia = valor.trim() === ''

          return (
            <div key={clave} className="min-w-0">
              <label
                htmlFor={`pregunta-${clave}`}
                className="mb-1 flex flex-wrap items-baseline gap-x-2 text-meta text-text"
              >
                <span className="rotulo shrink-0">
                  {def.bloque}
                  {nro}
                </span>
                <span className="min-w-0 flex-1">{pregunta}</span>
                {vacia ? (
                  <span className="inline-flex shrink-0 items-center gap-1 text-micro text-muted-strong">
                    <Icon name="reloj" size={10} />
                    Pendiente
                  </span>
                ) : null}
              </label>
              <textarea
                id={`pregunta-${clave}`}
                value={valor}
                rows={2}
                disabled={deshabilitado === true}
                onChange={(evento) => onCambio(clave, evento.target.value)}
                className="w-full resize-y rounded-md border border-border bg-surface px-2 py-1.5 text-meta leading-relaxed text-text placeholder:text-muted hover:border-n300 disabled:bg-n100"
                placeholder="Lo que dijo el cliente, en sus palabras"
              />
            </div>
          )
        })}
      </Stack>
    </fieldset>
  )
}
